import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";

const RealtimeContext = createContext(null);

const STATES = Object.freeze({
  CONNECTING: "connecting",
  LIVE: "live",
  RECONNECTING: "reconnecting",
  OFFLINE: "offline",
});

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function statusLabel(state) {
  if (state === STATES.LIVE) return "Live";
  if (state === STATES.RECONNECTING || state === STATES.CONNECTING) {
    return "Reconnecting…";
  }
  return "Offline";
}

function normalizeApiBase(auth) {
  const fromAuth = safeString(auth?.apiBase).trim();
  if (fromAuth) return fromAuth.replace(/\/+$/, "");

  if (typeof window !== "undefined") {
    return window.location.origin.replace(/\/+$/, "");
  }

  return "";
}

export function RealtimeProvider({ children }) {
  const auth = useAuth();
  const socketRef = useRef(null);
  const roMessageCreatedSubscribersRef = useRef(new Set());
  const roMessageRetractedSubscribersRef = useRef(new Set());
  const roAttachmentCreatedSubscribersRef = useRef(new Set());
  const roAttachmentRetractedSubscribersRef = useRef(new Set());
  const roMessagesReadSubscribersRef = useRef(new Set());

  const [state, setState] = useState(STATES.OFFLINE);
  const [socketId, setSocketId] = useState("");

  const token = safeString(auth?.token).trim();
  const effectiveStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();
  const apiBase = normalizeApiBase(auth);
  const hydrated = auth?.hydrated !== false;
  const isAuthenticated = !!auth?.isAuthenticated;
  const isStoreMode = auth?.mode !== "platform";

  const shouldConnect =
    hydrated && isAuthenticated && isStoreMode && !!token && !!effectiveStoreId;

  const subscribeToRoMessageCreated = useCallback((listener) => {
    if (typeof listener !== "function") return () => {};

    const subscribers = roMessageCreatedSubscribersRef.current;
    subscribers.add(listener);

    return () => {
      subscribers.delete(listener);
    };
  }, []);

  const subscribeToRoMessagesRead = useCallback((listener) => {
    if (typeof listener !== "function") return () => {};

    const subscribers = roMessagesReadSubscribersRef.current;
    subscribers.add(listener);

    return () => {
      subscribers.delete(listener);
    };
  }, []);

  const subscribeToRoMessageRetracted = useCallback((listener) => {
    if (typeof listener !== "function") return () => {};

    const subscribers = roMessageRetractedSubscribersRef.current;
    subscribers.add(listener);

    return () => {
      subscribers.delete(listener);
    };
  }, []);

  const subscribeToRoAttachmentRetracted = useCallback((listener) => {
    if (typeof listener !== "function") return () => {};

    const subscribers = roAttachmentRetractedSubscribersRef.current;
    subscribers.add(listener);

    return () => {
      subscribers.delete(listener);
    };
  }, []);

  const subscribeToRoAttachmentCreated = useCallback((listener) => {
    if (typeof listener !== "function") return () => {};

    const subscribers = roAttachmentCreatedSubscribersRef.current;
    subscribers.add(listener);

    return () => {
      subscribers.delete(listener);
    };
  }, []);

  useEffect(() => {
    const previousSocket = socketRef.current;
    if (previousSocket) {
      previousSocket.removeAllListeners();
      previousSocket.io?.removeAllListeners?.();
      previousSocket.disconnect();
      socketRef.current = null;
    }

    setSocketId("");

    if (!shouldConnect || !apiBase) {
      setState(STATES.OFFLINE);
      return undefined;
    }

    setState(STATES.CONNECTING);

    const socket = io(apiBase, {
      auth: {
        token,
        storeId: effectiveStoreId,
      },
      reconnection: true,
    });

    socketRef.current = socket;

    const manager = socket.io;

    function handleConnect() {
      setSocketId(socket.id || "");
      setState(STATES.LIVE);
    }

    function handleDisconnect(reason) {
      setSocketId("");

      if (reason === "io client disconnect" || socket.active === false) {
        setState(STATES.OFFLINE);
        return;
      }

      setState(STATES.RECONNECTING);
    }

    function handleConnectError() {
      setSocketId("");
      setState((current) =>
        current === STATES.RECONNECTING ? STATES.RECONNECTING : STATES.OFFLINE
      );
    }

    function handleReconnectAttempt() {
      setState(STATES.RECONNECTING);
    }

    function handleReconnectFailed() {
      setSocketId("");
      setState(STATES.OFFLINE);
    }

    function handleRoMessageCreated(payload) {
      const subscribers = Array.from(roMessageCreatedSubscribersRef.current);

      subscribers.forEach((listener) => {
        try {
          listener(payload);
        } catch (e) {
          console.error(
            "[Realtime] ro:message:created subscriber failed:",
            e
          );
        }
      });
    }

    function handleRoMessagesRead(payload) {
      const subscribers = Array.from(roMessagesReadSubscribersRef.current);

      subscribers.forEach((listener) => {
        try {
          listener(payload);
        } catch (e) {
          console.error(
            "[Realtime] ro:messages:read subscriber failed:",
            e
          );
        }
      });
    }

    function notifySubscribers(ref, label, payload) {
      const subscribers = Array.from(ref.current);

      subscribers.forEach((listener) => {
        try {
          listener(payload);
        } catch (e) {
          console.error(`[Realtime] ${label} subscriber failed:`, e);
        }
      });
    }

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.on("ro:message:created", handleRoMessageCreated);
    socket.on("ro:message:retracted", (payload) =>
      notifySubscribers(
        roMessageRetractedSubscribersRef,
        "ro:message:retracted",
        payload
      )
    );
    socket.on("ro:attachment:created", (payload) =>
      notifySubscribers(
        roAttachmentCreatedSubscribersRef,
        "ro:attachment:created",
        payload
      )
    );
    socket.on("ro:attachment:retracted", (payload) =>
      notifySubscribers(
        roAttachmentRetractedSubscribersRef,
        "ro:attachment:retracted",
        payload
      )
    );
    socket.on("ro:messages:read", handleRoMessagesRead);
    manager.on("reconnect_attempt", handleReconnectAttempt);
    manager.on("reconnect_failed", handleReconnectFailed);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("ro:message:created", handleRoMessageCreated);
      socket.off("ro:message:retracted");
      socket.off("ro:attachment:created");
      socket.off("ro:attachment:retracted");
      socket.off("ro:messages:read", handleRoMessagesRead);
      manager.off("reconnect_attempt", handleReconnectAttempt);
      manager.off("reconnect_failed", handleReconnectFailed);
      socket.disconnect();

      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [apiBase, effectiveStoreId, shouldConnect, token]);

  const value = useMemo(
    () => ({
      state,
      label: statusLabel(state),
      socketId,
      isLive: state === STATES.LIVE,
      isConnecting: state === STATES.CONNECTING,
      isReconnecting: state === STATES.RECONNECTING,
      isOffline: state === STATES.OFFLINE,
      isEnabled: shouldConnect,
      storeId: effectiveStoreId,
      subscribeToRoMessageCreated,
      subscribeToRoMessageRetracted,
      subscribeToRoAttachmentCreated,
      subscribeToRoAttachmentRetracted,
      subscribeToRoMessagesRead,
    }),
    [
      effectiveStoreId,
      shouldConnect,
      socketId,
      state,
      subscribeToRoMessageCreated,
      subscribeToRoMessageRetracted,
      subscribeToRoAttachmentCreated,
      subscribeToRoAttachmentRetracted,
      subscribeToRoMessagesRead,
    ]
  );

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  return (
    useContext(RealtimeContext) || {
      state: STATES.OFFLINE,
      label: "Offline",
      socketId: "",
      isLive: false,
      isConnecting: false,
      isReconnecting: false,
      isOffline: true,
      isEnabled: false,
      storeId: "",
      subscribeToRoMessageCreated: () => () => {},
      subscribeToRoMessageRetracted: () => () => {},
      subscribeToRoAttachmentCreated: () => () => {},
      subscribeToRoAttachmentRetracted: () => () => {},
      subscribeToRoMessagesRead: () => () => {},
    }
  );
}

